import { AllocatorActionType } from "@stakewise/v3-sdk";
import { NATIVE_ETH } from "../../../../shared/tokens";
import { ETHEREUM_BD_MAINNET_VAULT, normalizeEthVaultAddress, } from "../../../../shared/vault-addresses";
/**
 * Internal implementation for retrieving staker actions from StakeWise SDK.
 * @internal
 * @remarks
 * This function retrieves a list of interactions with the vault, providing comprehensive
 * transaction history for staking operations. It supports filtering by action types,
 * pagination, and user-specific queries.
 *
 * **Action Type Categories:**
 * - **Deposit Operations**: `Deposited`, `VaultCreated`
 * - **Withdrawal Operations**: `Redeemed`, `ExitedAssetsClaimed`
 * - **osToken Operations**: Uses `shares` field for transaction amounts
 * - **Standard Operations**: Uses `assets` field for transaction amounts
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance with network configuration
 * @param logger - Logger instance for operation tracking and debugging
 * @param request - Staker actions request with filtering and pagination parameters
 * @returns Promise resolving to array of staker actions with transaction metadata
 * @throws {Error} When vault address is invalid or not configured
 * @throws {Error} When StakeWise SDK fails to retrieve actions
 *
 * @see {@link https://sdk.stakewise.io/vault/requests/getstakeractions} StakeWise SDK Documentation
 *
 */
export async function _getStakerActions(stakewiseSDK, logger, request) {
    const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
    if (!vaultAddress)
        throw new Error("Vault address not found");
    // Apply defaults for optional parameters
    const limit = request.limit ?? 100;
    const skip = request.skip ?? 0;
    // When no types are specified, use all possible action types to get all actions
    const allActionTypes = Object.values(AllocatorActionType);
    const types = request.types || allActionTypes;
    logger.debug("Retrieving staker actions from StakeWise SDK", {
        vaultAddress,
        userAddress: request.userAddress,
        types: request.types ? request.types : "all",
        limit,
        skip,
    });
    const actions = await stakewiseSDK.vault.getStakerActions({
        vaultAddress,
        userAddress: request.userAddress,
        types,
        limit,
        skip,
    });
    logger.debug("Successfully retrieved staker actions", {
        actionCount: actions.length,
    });
    // Map the actions to our expected format
    return actions.map((action) => ({
        actionType: action.actionType,
        createdAt: action.createdAt,
        assets: action.assets,
        shares: action.shares,
        link: action.link,
        id: action.id,
    }));
}
/**
 * Internal implementation for retrieving stake balance from StakeWise SDK.
 *
 * @remarks
 * This function retrieves the user's current balance in the vault, including both
 * the current staked assets and total earned rewards over time. The balance represents
 * the user's position in the liquid staking vault.
 *
 * **Technical Implementation Details:**
 * - Uses StakeWise SDK's `vault.getStakeBalance()` method
 * - Returns balance in ETH (wei) as `bigint` for precision
 * - Calculates total earned rewards since initial deposit
 * - Reflects real-time vault performance and user's share
 *
 * **Balance Components:**
 * - **asset**: Token information for ETH
 * - **assets**: Balance in ETH (current staked balance)
 * - **totalEarnedAssets**: Total earned rewards (sum of staking rewards + boost rewards)
 * - **metadata.totalStakeEarnedAssets**: Total earned assets from staking specifically
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance with network configuration
 * @param logger - Logger instance for operation tracking and debugging
 * @param request - Stake balance request with user and vault identification
 * @returns Promise resolving to current stake balance and total earned rewards
 * @throws {Error} When vault address is invalid or not configured
 * @throws {Error} When user address is invalid or has no balance
 * @throws {Error} When StakeWise SDK fails to retrieve balance data
 *
 * @see {@link https://sdk.stakewise.io/vault/requests/getstakebalance} StakeWise SDK Documentation
 *
 * @internal
 */
export async function _getStakeBalance(stakewiseSDK, logger, request) {
    const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
    if (!vaultAddress)
        throw new Error("Vault address not found");
    logger.debug("Retrieving stake balance from StakeWise SDK", {
        vaultAddress,
        userAddress: request.userAddress,
    });
    const balance = await stakewiseSDK.vault.getStakeBalance({
        vaultAddress,
        userAddress: request.userAddress,
    });
    logger.debug("Successfully retrieved stake balance", {
        assets: balance.assets?.toString(),
        totalEarnedAssets: balance.totalEarnedAssets?.toString(),
        totalStakeEarnedAssets: balance.totalStakeEarnedAssets?.toString(),
    });
    // Return the IGetStakeBalanceResponse structure with ETH token info and Stakewise metadata
    return {
        asset: {
            ...NATIVE_ETH,
            balance: balance.assets?.toString(),
        },
        assets: balance.assets,
        totalEarnedAssets: balance.totalEarnedAssets,
        metadata: {
            totalStakeEarnedAssets: balance.totalStakeEarnedAssets,
        },
    };
}
/**
 * Internal implementation for retrieving user rewards from StakeWise SDK.
 *
 * @remarks
 * This function retrieves detailed reward history for a user over a specified time period,
 * providing daily reward breakdowns with multi-currency support (ETH, USD, EUR).
 * Essential for reward tracking, tax reporting, and performance analysis.
 *
 * **Technical Implementation Details:**
 * - Uses StakeWise SDK's `vault.getUserRewards()` method
 * - Supports date range filtering with `dateFrom` and `dateTo` parameters
 * - Returns daily reward entries with timestamp and amount data
 * - Includes multi-currency conversion for accounting purposes
 *
 * **Date Range Handling:**
 * - Dates should be in ISO 8601 format (YYYY-MM-DD)
 * - Maximum range typically limited to 1 year for performance
 * - Returns empty array if no rewards in specified period
 * - Timezone handling follows vault's configured timezone
 *
 * **Reward Calculation Logic:**
 * - Daily rewards calculated based on user's vault share
 * - Reflects validator performance and network conditions
 * - Accounts for vault fees and performance charges
 * - Includes both consensus and execution layer rewards
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance with network configuration
 * @param logger - Logger instance for operation tracking and debugging
 * @param request - User rewards request with date range and identification parameters
 * @returns Promise resolving to array of daily reward entries with multi-currency data
 * @throws {Error} When vault address is invalid or not configured
 * @throws {Error} When date range is invalid or exceeds limits
 * @throws {Error} When StakeWise SDK fails to retrieve reward data
 *
 * @internal
 */
export async function _getUserRewards(stakewiseSDK, logger, request) {
    const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
    if (!vaultAddress)
        throw new Error("Vault address not found");
    logger.debug("Retrieving user rewards from StakeWise SDK", {
        vaultAddress,
        userAddress: request.userAddress,
        dateFrom: request.dateFrom,
        dateTo: request.dateTo,
    });
    const rewards = await stakewiseSDK.vault.getUserRewards({
        vaultAddress,
        userAddress: request.userAddress,
        dateFrom: request.dateFrom,
        dateTo: request.dateTo,
    });
    logger.debug("Successfully retrieved user rewards", {
        rewardCount: rewards.length,
    });
    return rewards;
}
/**
 * Internal implementation for claiming rewards from StakeWise SDK.
 *
 * @remarks
 * This function builds a transaction for claiming accumulated staking rewards.
 * The implementation handles reward splitter integration and transaction construction
 * for on-chain reward claiming operations.
 *
 * **Technical Implementation Details:**
 * - Uses StakeWise SDK's `vault.claimRewards()` method (if available)
 * - Builds transaction data for reward claiming smart contract interaction
 * - Supports reward splitter addresses for complex reward distribution
 * - Returns transaction object ready for signing and broadcasting
 *
 * **Reward Claiming Process:**
 * 1. Validates reward splitter and vault addresses
 * 2. Calculates claimable reward amounts
 * 3. Builds transaction with proper gas estimation
 * 4. Returns transaction data for user signing
 *
 * **Transaction Structure:**
 * - `to`: Target contract address (reward splitter or vault)
 * - `data`: Encoded function call with claim parameters
 * - `value`: ETH value (typically 0 for reward claims)
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance with network configuration
 * @param logger - Logger instance for operation tracking and debugging
 * @param request - Claim rewards request with reward splitter and amount parameters
 * @returns Promise resolving to transaction data for reward claiming
 * @throws {Error} When vault address is invalid or not configured
 * @throws {Error} When reward splitter address is invalid
 * @throws {Error} When claim amount exceeds available rewards
 * @throws {Error} When StakeWise SDK method is not available
 *
 * @internal
 */
export async function _claimRewards(stakewiseSDK, logger, request) {
    const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
    if (!vaultAddress)
        throw new Error("Vault address not found");
    const extVault = stakewiseSDK.vault;
    if (!extVault.claimRewards) {
        logger.error("ClaimRewards method not available in SDK", {
            vaultAddress,
            userAddress: request.userAddress,
        });
        throw new Error("ClaimRewards method not available in SDK");
    }
    logger.debug("Claiming rewards from StakeWise SDK", {
        vaultAddress,
        userAddress: request.userAddress,
        rewardSplitterAddress: request.rewardSplitterAddress,
        assets: request.assets.toString(),
    });
    try {
        const claimTx = await extVault.claimRewards({
            vaultAddress,
            userAddress: request.userAddress,
            rewardSplitterAddress: request.rewardSplitterAddress,
            assets: request.assets,
        });
        logger.debug("Successfully built claim rewards transaction", {
            to: claimTx.to,
            dataLength: claimTx.data?.length,
            value: claimTx.value?.toString(),
        });
        return {
            to: claimTx.to,
            data: claimTx.data,
            value: claimTx.value,
        };
    }
    catch (error) {
        logger.error("Failed to claim rewards", {
            error: error instanceof Error ? error.message : "Unknown error",
            request: {
                vaultAddress,
                userAddress: request.userAddress,
                rewardSplitterAddress: request.rewardSplitterAddress,
                assets: request.assets.toString(),
            },
        });
        throw new Error(`Failed to claim rewards: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
}
/**
 * Internal implementation for estimating claim rewards gas from StakeWise SDK.
 *
 * @remarks
 * This function estimates the gas cost for claiming rewards, providing accurate
 * gas usage predictions for reward claiming transactions. Essential for cost
 * calculation and transaction fee planning.
 *
 * **Technical Implementation Details:**
 * - Uses StakeWise SDK's `vault.estimateClaimRewardsGas()` method (if available)
 * - Simulates reward claiming transaction to calculate gas usage
 * - Returns gas estimate in gas units (not wei or gwei)
 * - Accounts for current network conditions and contract complexity
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance with network configuration
 * @param logger - Logger instance for operation tracking and debugging
 * @param request - Claim rewards request for gas estimation parameters
 * @returns Promise resolving to estimated gas amount in gas units
 * @throws {Error} When vault address is invalid or not configured
 * @throws {Error} When reward splitter address is invalid
 * @throws {Error} When claim parameters are invalid for estimation
 * @throws {Error} When StakeWise SDK method is not available
 *
 * @internal
 */
export async function _estimateClaimRewardsGas(stakewiseSDK, logger, request) {
    const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
    if (!vaultAddress)
        throw new Error("Vault address not found");
    const extVault = stakewiseSDK.vault;
    if (!extVault.estimateClaimRewardsGas) {
        logger.error("EstimateClaimRewardsGas method not available in SDK", {
            vaultAddress,
            userAddress: request.userAddress,
        });
        throw new Error("EstimateClaimRewardsGas method not available in SDK");
    }
    logger.debug("Estimating claim rewards gas from StakeWise SDK", {
        vaultAddress,
        userAddress: request.userAddress,
        rewardSplitterAddress: request.rewardSplitterAddress,
        assets: request.assets.toString(),
    });
    try {
        const gasEstimate = await extVault.estimateClaimRewardsGas({
            vaultAddress,
            userAddress: request.userAddress,
            rewardSplitterAddress: request.rewardSplitterAddress,
            assets: request.assets,
        });
        logger.debug("Successfully estimated claim rewards gas", {
            gasEstimate: gasEstimate.toString(),
        });
        return gasEstimate;
    }
    catch (error) {
        logger.error("Failed to estimate claim rewards gas", {
            error: error instanceof Error ? error.message : "Unknown error",
            request: {
                vaultAddress,
                userAddress: request.userAddress,
                rewardSplitterAddress: request.rewardSplitterAddress,
                assets: request.assets.toString(),
            },
        });
        throw new Error(`Failed to estimate claim rewards gas: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
}
//# sourceMappingURL=data:application/json;base64,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